import { getCurrentCopy } from '../i18n/current';

/**
 * Sanitização de mensagens de erro
 * Mapeia erros do Supabase para mensagens amigáveis ao usuário
 * Em produção, erros desconhecidos retornam mensagem genérica
 *
 * O texto NÃO mora mais aqui: mora em `auth.errors` do dicionário, indexado
 * pelas chaves abaixo. Este arquivo é regra (qual substring do Supabase
 * corresponde a qual erro), e regra não muda com o idioma. Isso também acaba
 * com a duplicação que existia entre este mapa e o de códigos de analytics,
 * que tinham as mesmas chaves e podiam divergir sem ninguém perceber.
 */

type ErrorKey = keyof ReturnType<typeof getCurrentCopy>['auth']['errors'];

/**
 * A ordem importa: o casamento é por `includes`, e a primeira entrada que bate
 * vence. Mantida igual à original.
 */
const MESSAGE_KEY_MAP: Record<string, ErrorKey> = {
  'Invalid login credentials': 'invalid_credentials',
  'Email not confirmed': 'email_not_confirmed',
  'Too many requests': 'rate_limit',
  'User already registered': 'user_already_registered',
  'Password should be at least': 'weak_password',
  'Email rate limit exceeded': 'email_rate_limit',
  // Mensagem própria, e não `rate_limit`: o Supabase manda isto no cooldown de
  // reenvio, e "aguarde alguns minutos" daria um prazo que não é o real.
  'For security purposes': 'rate_limit_security',
  'New password should be different': 'password_unchanged',
  'Auth session missing': 'session_missing',
  'Token has expired': 'token_expired',
  'otp_expired': 'token_expired',
  'access_denied': 'access_denied',
  'User not found': 'user_not_found',
  'Email link is invalid': 'invalid_link',
  'Unable to validate email address': 'invalid_email',
  'provider is not enabled': 'provider_disabled',
  // Duas variantes de captcha com saídas diferentes de propósito: a do widget
  // resolve tentando de novo, a nossa exige recarregar a página.
  'captcha verification failed': 'captcha_verification_failed',
  'captcha_failed': 'captcha_failed',
};

// Códigos estáveis e SEM PII para analytics (GA4). Mesmas chaves do ERROR_MAP,
// mas o valor é um código de máquina (não a mensagem do usuário). Usado por
// classifyAuthError para o parâmetro error_code do evento auth_error.
const ERROR_CODE_MAP: Record<string, string> = {
  'Invalid login credentials': 'invalid_credentials',
  'Email not confirmed': 'email_not_confirmed',
  'Too many requests': 'rate_limit',
  'User already registered': 'user_already_registered',
  'Password should be at least': 'weak_password',
  'Email rate limit exceeded': 'email_rate_limit',
  'For security purposes': 'rate_limit',
  'New password should be different': 'password_unchanged',
  'Auth session missing': 'session_missing',
  'Token has expired': 'token_expired',
  'otp_expired': 'token_expired',
  'access_denied': 'access_denied',
  'User not found': 'user_not_found',
  'Email link is invalid': 'invalid_link',
  'Unable to validate email address': 'invalid_email',
  'provider is not enabled': 'provider_disabled',
  'captcha verification failed': 'captcha_failed',
  'captcha_failed': 'captcha_failed',
};

interface ErrorLike {
  message?: string;
  error_description?: string;
  code?: string;
  details?: string;
  hint?: string;
}

// Extrai a string de mensagem de um erro (string, Error, ou objeto do Supabase).
function extractMessage(error: unknown): string {
  if (!error) return '';
  return typeof error === 'string'
    ? error
    : (error as ErrorLike).message || (error as ErrorLike).error_description || '';
}

/**
 * Descrição legível de um erro, para toast de diagnóstico.
 *
 * Existe porque `String(err)` num erro do Supabase devolve "[object Object]":
 * PostgrestError é um objeto simples (`{ message, code, details, hint }`), não
 * uma instância de `Error`. Um toast dizendo "[object Object]" é pior do que
 * não ter toast, porque some com a pista em vez de mostrá-la.
 *
 * Diferente de `sanitizeError`, que traduz para copy de autenticação e esconde
 * o cru em produção: aqui o objetivo é reportar o que o banco respondeu.
 */
export function describeError(error: unknown): string {
  if (!error) return '';

  const message = extractMessage(error);
  const like = (typeof error === 'object' ? error : {}) as ErrorLike;
  const parts = [message || like.details || like.hint || ''].filter(Boolean);

  if (like.code) parts.push(`(${like.code})`);
  if (parts.length > 0) return parts.join(' ');

  // Último recurso: um objeto sem nenhum campo conhecido ainda diz mais
  // serializado do que como "[object Object]".
  try {
    return JSON.stringify(error);
  } catch {
    return '';
  }
}

/**
 * Sanitiza mensagem de erro para exibição ao usuário
 * @param {string|Error|object} error
 * @returns {string}
 */
export function sanitizeError(error: unknown): string {
  const errors = getCurrentCopy().auth.errors;
  if (!error) return errors.unknown;

  const msg: string = extractMessage(error);

  for (const [needle, key] of Object.entries(MESSAGE_KEY_MAP)) {
    if (msg.includes(needle)) return errors[key];
  }

  // Em produção, nunca expor erro raw
  if (typeof import.meta !== 'undefined' && import.meta.env?.PROD) {
    return errors.unknown;
  }

  // Em dev, mostrar mensagem original para debug
  return msg || errors.unknown;
}

/**
 * Classifica um erro de autenticação num código estável e SEM PII, para o
 * parâmetro error_code do evento de analytics. Irmão de sanitizeError, mas:
 *  - retorna um código de máquina (não a mensagem pt-BR);
 *  - NÃO aplica o branch "PROD → genérico" (o código não tem PII, pode ir em prod);
 *  - falha de rede vira 'network'; sem match conhecido vira 'unknown'.
 * @param {string|Error|object} error
 * @returns {string} código (ex.: 'invalid_credentials', 'captcha_failed', 'network', 'unknown')
 */
export function classifyAuthError(error: unknown): string {
  if (!error) return 'unknown';

  const msg: string = extractMessage(error);

  for (const [key, code] of Object.entries(ERROR_CODE_MAP)) {
    if (msg.includes(key)) return code;
  }

  // Falha de rede (fetch abortado/offline) — comum em conexões móveis instáveis.
  const name: string = error instanceof Error ? error.name : '';
  if (
    name === 'TypeError' ||
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Network request failed') ||
    msg.includes('network')
  ) {
    return 'network';
  }

  return 'unknown';
}
